import { useState, useEffect } from "react";
import { getColors, checkpoints } from "./constants";
import { ColorsContext } from "./ColorsContext";

import ScreenWelcome          from "./screens/ScreenWelcome";
import ScreenThemeSelect      from "./screens/ScreenThemeSelect";
import ScreenQuiz             from "./screens/ScreenQuiz";
import ScreenBrief            from "./screens/ScreenBrief";
import ScreenAttract          from "./screens/ScreenAttract";
import ScreenCheckpointPuzzle from "./screens/ScreenCheckpointPuzzle";
import ScreenCheckpointAction from "./screens/ScreenCheckpointAction";
import ScreenCheckpointGamer  from "./screens/ScreenCheckpointGamer";
import ScreenFinale           from "./screens/ScreenFinale";
import RecapScreen            from "./components/RecapCards";

export default function App() {
  const [screen, setScreen]               = useState("welcome");
  const [selectedPath, setSelectedPath]   = useState(null);
  const [cpIndex, setCpIndex]             = useState(0);
  const [xpTotal, setXpTotal]             = useState(0);
  const [splitTimes, setSplitTimes]       = useState([]);
  const [allyResults, setAllyResults]     = useState([]);
  const [gearUnlocked, setGearUnlocked]   = useState([]);
  const [recommendedPath, setRecommended] = useState(null);

  const C = getColors(false);

  // ── Viewport scaling ───────────────────────────────────
  const [vp, setVp] = useState({ w: window.innerWidth, h: window.innerHeight });
  useEffect(() => {
    const onResize = () => setVp({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  // Phone (<600px): fill full width; tablet/desktop: cap so logical height stays usable
  const scale   = vp.w < 600
    ? vp.w / 390
    : Math.min(vp.w / 390, vp.h / 750);
  const offsetX = Math.max(0, (vp.w - 390 * scale) / 2);

  // ── Navigation helpers ─────────────────────────────────
  function handleSelectPath(p) {
    setSelectedPath(p);
    setCpIndex(0);
    setXpTotal(0);
    setSplitTimes([]);
    setAllyResults([]);
    setGearUnlocked([]);
    setScreen("brief");
  }

  function handleCheckpointComplete({ xp = 0, splitTime, allySaved, gear }) {
    setXpTotal(x => x + xp);
    if (splitTime !== undefined) setSplitTimes(t => [...t, splitTime]);
    if (allySaved !== undefined) setAllyResults(r => [...r, allySaved]);
    if (gear)                    setGearUnlocked(g => [...g, gear]);

    if (cpIndex + 1 < checkpoints.length) {
      setCpIndex(i => i + 1);
      setScreen("attract");
    } else {
      setScreen("finale");
    }
  }

  function handleRestart() {
    setScreen("welcome");
    setSelectedPath(null);
    setCpIndex(0);
    setXpTotal(0);
    setSplitTimes([]);
    setAllyResults([]);
    setGearUnlocked([]);
    setRecommended(null);
  }

  // ── Render correct checkpoint screen ──────────────────
  function renderCheckpoint() {
    const cp = checkpoints[cpIndex];
    const lay = { scale, offsetX, vpWidth: vp.w };
    const common = {
      path: selectedPath,
      checkpoint: cp,
      checkpointIndex: cpIndex,
      totalCheckpoints: checkpoints.length,
      onComplete: handleCheckpointComplete,
      onBack: () => setScreen("attract"),
      layout: lay,
    };
    if (selectedPath.id === "puzzle") return <ScreenCheckpointPuzzle {...common} />;
    if (selectedPath.id === "action") return <ScreenCheckpointAction {...common} allyResults={allyResults} />;
    if (selectedPath.id === "gamer")  return <ScreenCheckpointGamer  {...common} xpTotal={xpTotal} gearUnlocked={gearUnlocked} />;
    return null;
  }

  return (
    <ColorsContext.Provider value={C}>
      <div style={{ width: "100vw", height: "100dvh", overflow: "hidden", position: "relative", background: C.navy }}>
      <div style={{
        width: 390,
        height: vp.h / scale,
        transform: `scale(${scale})`,
        transformOrigin: "top left",
        position: "absolute",
        top: 0,
        left: offsetX,
        fontFamily: "'Segoe UI', system-ui, sans-serif",
        background: C.navy, overflow: "visible",
      }}>
        {screen === "welcome" && (
          <ScreenWelcome onNext={() => setScreen("select")} />
        )}
        {screen === "select" && (
          <ScreenThemeSelect
            recommended={recommendedPath}
            onSelect={handleSelectPath}
            onQuiz={() => setScreen("quiz")}
            onBack={() => setScreen("welcome")}
            layout={{ scale, offsetX, vpWidth: vp.w }}
          />
        )}
        {screen === "quiz" && (
          <ScreenQuiz
            onResult={id => { setRecommended(id); setScreen("select"); }}
            onBack={() => setScreen("select")}
          />
        )}
        {screen === "brief" && selectedPath && (
          <ScreenBrief
            path={selectedPath}
            onNext={() => setScreen("attract")}
            onBack={() => setScreen("select")}
            layout={{ scale, offsetX, vpWidth: vp.w }}
          />
        )}
        {screen === "attract" && selectedPath && (
          <ScreenAttract
            path={selectedPath}
            checkpoint={checkpoints[cpIndex]}
            onActivate={() => setScreen("checkpoint")}
            onBack={() => setScreen("brief")}
            layout={{ scale, offsetX, vpWidth: vp.w }}
          />
        )}
        {screen === "checkpoint" && selectedPath && renderCheckpoint()}
        {screen === "finale" && selectedPath && (
          <ScreenFinale
            path={selectedPath}
            xpEarned={xpTotal}
            allyResults={allyResults}
            gearUnlocked={gearUnlocked}
            splitTimes={splitTimes}
            onRestart={handleRestart}
            onShare={() => setScreen("recap")}
            layout={{ scale, offsetX, vpWidth: vp.w }}
          />
        )}
      </div>
      {screen === "recap" && selectedPath && (
        <RecapScreen
          path={selectedPath}
          xpEarned={xpTotal}
          allyResults={allyResults}
          gearUnlocked={gearUnlocked}
          splitTimes={splitTimes}
          onClose={() => setScreen("finale")}
          vpWidth={vp.w}
        />
      )}
      </div>
    </ColorsContext.Provider>
  );
}
